import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTools } from '../context/ToolContext';
import ToolCard from '../components/Home/ToolCard';
import slugify from '../utils/slugify';
import { 
  Search, 
  Folder, 
  Grid, 
  List, 
  ArrowRight, 
  Sparkles, 
  TrendingUp, 
  Zap,
  Clock,
  Star,
  Hash,
  X
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';

// Constants
const POPULAR_SEARCHES = [
  'ChatGPT Alternative',
  'Self-Hosted AI',
  'Image Generation',
  'Code Assistant',
  'Video Editing AI',
  'Free AI Tools',
  'Voice Synthesis',
  'AI Chatbots'
] as const;

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toolCategories } = useTools();
  const query = searchParams.get('q') || '';
  
  const [searchInput, setSearchInput] = useState(query);
  const [searchResults, setSearchResults] = useState<{
    tools: any[];
    categories: any[];
  }>({ tools: [], categories: [] });
  
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isSearching, setIsSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout>();

  // Memoized calculations
  const featuredCategories = useMemo(() => 
    toolCategories
      .filter(cat => cat.tools.length > 0)
      .slice(0, 6),
    [toolCategories]
  );

  const totalTools = useMemo(() => 
    toolCategories.reduce((sum, cat) => sum + cat.tools.length, 0),
    [toolCategories]
  );

  const totalResults = useMemo(() => 
    searchResults.tools.length + searchResults.categories.length,
    [searchResults]
  );

  // Memoized SEO data
  const seoData = useMemo(() => {
    const hasQuery = query.trim();
    const resultsCount = searchResults.tools.length + searchResults.categories.length;
    
    if (hasQuery) {
      return {
        title: `Search Results for "${query}" - Find AI Tools | TooliQ.ai`,
        description: `Discover ${resultsCount} AI tools matching "${query}". Browse free & paid AI solutions for productivity, creativity, and business. Updated daily.`,
        keywords: `${query}, AI tools, artificial intelligence software, ${searchResults.tools.slice(0, 3).map(t => t.title).join(', ')}, free AI tools`,
        canonical: `https://tooliq.ai/search?q=${encodeURIComponent(query)}`,
        structuredData: resultsCount > 0 ? {
          "@context": "https://schema.org",
          "@type": "SearchResultsPage",
          "name": `Search Results for "${query}"`,
          "description": `Search results for ${query} on TooliQ.ai`,
          "url": `https://tooliq.ai/search?q=${encodeURIComponent(query)}`,
          "mainEntity": {
            "@type": "ItemList",
            "numberOfItems": searchResults.tools.length,
            "itemListElement": searchResults.tools.slice(0, 10).map((tool, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "item": {
                "@type": "SoftwareApplication",
                "name": tool.title,
                "description": tool.description,
                "applicationCategory": "AI Tool"
              }
            }))
          }
        } : null
      };
    } else {
      return {
        title: 'Advanced AI Tools Search - Discover 10,000+ AI Applications | TooliQ.ai',
        description: 'Search through 10,000+ AI tools across 100+ categories. Find the perfect artificial intelligence solution for your needs with our comprehensive directory.',
        keywords: 'AI tools search, find AI applications, artificial intelligence directory, search AI software, browse AI tools',
        canonical: 'https://tooliq.ai/search',
        structuredData: {
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "TooliQ.ai AI Tools Search",
          "url": "https://tooliq.ai",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://tooliq.ai/search?q={search_term_string}",
            "query-input": "required name=search_term_string"
          },
          "description": "Search thousands of AI tools and applications"
        }
      };
    }
  }, [query, searchResults.tools, searchResults.categories.length]);

  // Helper function to calculate relevance
  const calculateRelevance = useCallback((text: string, searchTerm: string): number => {
    if (text.toLowerCase() === searchTerm.toLowerCase()) return 100;
    if (text.toLowerCase().startsWith(searchTerm.toLowerCase())) return 80;
    if (text.toLowerCase().includes(searchTerm.toLowerCase())) return 60;
    return 40;
  }, []);

  // Search function - memoized
  const performSearch = useCallback((searchTerm: string) => {
    if (!searchTerm) {
      setSearchResults({ tools: [], categories: [] });
      return;
    }

    const toolsResults: any[] = [];
    const categoriesResults: any[] = [];
    const lowerSearchTerm = searchTerm.toLowerCase();

    // Single pass through categories
    toolCategories.forEach(category => {
      // Category match
      if (category.title.toLowerCase().includes(lowerSearchTerm)) {
        categoriesResults.push({
          ...category,
          matchType: 'category',
          relevance: calculateRelevance(category.title, searchTerm)
        });
      }

      // Tool matches
      category.tools.forEach((tool: any) => {
        let relevance = 0;
        let matchField = '';

        // Title match (highest relevance)
        if (tool.title.toLowerCase().includes(lowerSearchTerm)) {
          relevance += 100;
          matchField = 'title';
        }
        
        // Description match
        if (tool.description?.toLowerCase().includes(lowerSearchTerm)) {
          relevance += 50;
          matchField = matchField || 'description';
        }

        // Tags match
        if (tool.tags?.some((tag: string) => tag.toLowerCase().includes(lowerSearchTerm))) {
          relevance += 30;
          matchField = matchField || 'tags';
        }

        // Features match
        if (tool.features?.some((feature: string) => feature.toLowerCase().includes(lowerSearchTerm))) {
          relevance += 20;
          matchField = matchField || 'features';
        }

        if (relevance > 0) {
          toolsResults.push({
            ...tool,
            category: category.title,
            categorySlug: slugify(category.title),
            matchType: 'tool',
            relevance,
            matchField
          });
        }
      });
    });

    // Sort by relevance
    toolsResults.sort((a, b) => b.relevance - a.relevance);
    categoriesResults.sort((a, b) => b.relevance - a.relevance);

    setSearchResults({
      tools: toolsResults,
      categories: categoriesResults
    });
  }, [toolCategories, calculateRelevance]);

  // Handle search with debouncing
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (query.trim()) {
      setIsSearching(true);
      const timer = setTimeout(() => {
        performSearch(query.trim().toLowerCase());
        setIsSearching(false);
      }, 300);
      
      setSearchTimeout(timer);
    } else {
      setSearchResults({ tools: [], categories: [] });
      setIsSearching(false);
    }

    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [query, performSearch]);

  // Event handlers
  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const trimmedInput = searchInput.trim();
    if (trimmedInput) {
      setSearchParams({ q: trimmedInput });
    }
  }, [searchInput, setSearchParams]);

  const handleQuickSearch = useCallback((term: string) => {
    setSearchInput(term);
    setSearchParams({ q: term });
  }, [setSearchParams]);

  const handleClearSearch = useCallback(() => {
    setSearchInput('');
    setSearchParams({});
  }, [setSearchParams]);

  const handleCategoryClick = useCallback((categoryTitle: string) => {
    const slug = slugify(categoryTitle);
    navigate(`/categories/${slug}`);
  }, [navigate]);

  const handleTagClick = useCallback((tag: string) => {
    handleQuickSearch(tag);
  }, [handleQuickSearch]);

  const toggleViewMode = useCallback((mode: 'grid' | 'list') => {
    setViewMode(mode);
  }, []);

  // Render components based on whether there's a query
  if (!query) {
    return <SearchDiscoveryPage 
      searchInput={searchInput}
      onSearchInputChange={setSearchInput}
      onSearch={handleSearch}
      onClearSearch={handleClearSearch}
      onQuickSearch={handleQuickSearch}
      onCategoryClick={handleCategoryClick}
      totalTools={totalTools}
      toolCategoriesCount={toolCategories.length}
      featuredCategories={featuredCategories}
      seoData={seoData}
    />;
  }

  return (
    <SearchResultsPage 
      query={query}
      searchInput={searchInput}
      onSearchInputChange={setSearchInput}
      onSearch={handleSearch}
      onClearSearch={handleClearSearch}
      searchResults={searchResults}
      totalResults={totalResults}
      isSearching={isSearching}
      viewMode={viewMode}
      onToggleViewMode={toggleViewMode}
      onQuickSearch={handleQuickSearch}
      onCategoryClick={handleCategoryClick}
      onTagClick={handleTagClick}
      seoData={seoData}
    />
  );
};

// Extracted SearchDiscoveryPage component
interface SearchDiscoveryPageProps {
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  onSearch: (e: React.FormEvent) => void;
  onClearSearch: () => void;
  onQuickSearch: (term: string) => void;
  onCategoryClick: (title: string) => void;
  totalTools: number;
  toolCategoriesCount: number;
  featuredCategories: any[];
  seoData: any;
}

const SearchDiscoveryPage: React.FC<SearchDiscoveryPageProps> = ({
  searchInput,
  onSearchInputChange,
  onSearch,
  onClearSearch,
  onQuickSearch,
  onCategoryClick,
  totalTools,
  toolCategoriesCount,
  featuredCategories,
  seoData
}) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50/50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900/10">
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href={seoData.canonical} />
        
        {/* Open Graph */}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:url" content={seoData.canonical} />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(seoData.structuredData)}
        </script>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Main Search Section */}
        <div className="text-center py-12 lg:py-20">
          <div className="max-w-4xl mx-auto">
            <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-600 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-lg">
              <Search className="w-10 h-10 text-white" />
            </div>
            
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              Discover <span className="text-transparent bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text">AI Tools</span>
            </h1>
            
            <p className="text-xl lg:text-2xl text-gray-600 dark:text-gray-400 mb-12 max-w-3xl mx-auto leading-relaxed">
              Search through thousands of AI tools, applications, and categories to find exactly what you need
            </p>

            {/* Search Box - KEY SEO ELEMENT */}
            <SearchBox 
              value={searchInput}
              onChange={onSearchInputChange}
              onSubmit={onSearch}
              onClear={onClearSearch}
              placeholder="Search for AI tools, categories, features... (e.g., 'ChatGPT alternative', 'image generation')"
              autoFocus
            />
          </div>
        </div>

        {/* SEO Content Section */}
        <SEOContentSection totalTools={totalTools} toolCategoriesCount={toolCategoriesCount} />

        {/* Quick Access Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Popular Searches */}
          <QuickSection 
            title="Popular Searches"
            subtitle="What others are looking for"
            icon={<TrendingUp className="w-6 h-6 text-white" />}
            iconBg="from-orange-500 to-red-500"
            items={POPULAR_SEARCHES.map(term => ({ label: term, onClick: () => onQuickSearch(term) }))}
          />

          {/* Quick Categories */}
          <QuickSection 
            title="Browse Categories"
            subtitle="Explore by category"
            icon={<Folder className="w-6 h-6 text-white" />}
            iconBg="from-green-500 to-emerald-600"
            items={featuredCategories.map(cat => ({ 
              label: cat.title, 
              subtitle: `${cat.tools.length} tools`,
              onClick: () => onCategoryClick(cat.title)
            }))}
          />
        </div>

        {/* Stats & Features */}
        <StatsSection totalTools={totalTools} toolCategoriesCount={toolCategoriesCount} />

        {/* CTA Section */}
        <CTASection onBrowseCategories={() => navigate('/categories')} onViewTools={() => navigate('/tools')} />
      </div>
    </div>
  );
};

// Extracted SearchBox component
interface SearchBoxProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onClear: () => void;
  placeholder: string;
  autoFocus?: boolean;
}

const SearchBox: React.FC<SearchBoxProps> = ({
  value,
  onChange,
  onSubmit,
  onClear,
  placeholder,
  autoFocus = false
}) => (
  <form onSubmit={onSubmit} className="max-w-2xl mx-auto mb-16">
    <div className="relative group">
      <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
        <div className="flex items-center px-6 py-4">
          <Search className="w-6 h-6 text-gray-400 mr-4 flex-shrink-0" />
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="flex-1 bg-transparent border-0 text-lg placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white focus:ring-0 outline-none"
            autoFocus={autoFocus}
          />
          {value && (
            <button
              type="button"
              onClick={onClear}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Clear search"
            >
              <X className="w-5 h-5" />
            </button>
          )}
          <button
            type="submit"
            disabled={!value.trim()}
            className="ml-4 px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            Search
          </button>
        </div>
      </div>
    </div>
  </form>
);

// Extracted SEOContentSection component
interface SEOContentSectionProps {
  totalTools: number;
  toolCategoriesCount: number;
}

const SEOContentSection: React.FC<SEOContentSectionProps> = ({ totalTools, toolCategoriesCount }) => (
  <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/10 dark:to-purple-900/10 rounded-3xl p-8 mb-12 border border-gray-200/50 dark:border-gray-700/50">
    <div className="prose dark:prose-invert max-w-none">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
        Find the Perfect AI Tool for Your Needs
      </h2>
      <p className="text-gray-700 dark:text-gray-300 mb-4 text-lg">
        TooliQ.ai's search directory contains <strong>{totalTools}+ AI tools</strong> across <strong>{toolCategoriesCount} categories</strong>. 
        Whether you're looking for <strong>free AI tools</strong>, <strong>AI for business</strong>, <strong>creative AI applications</strong>, 
        or <strong>productivity AI software</strong>, our comprehensive search helps you discover the best solutions.
      </p>
      <p className="text-gray-700 dark:text-gray-300 text-lg">
        Use our advanced search to filter by category, pricing, features, and more. Updated daily with new AI tools and applications.
      </p>
    </div>
  </div>
);

// Extracted QuickSection component
interface QuickSectionItem {
  label: string;
  subtitle?: string;
  onClick: () => void;
}

interface QuickSectionProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  iconBg: string;
  items: QuickSectionItem[];
}

const QuickSection: React.FC<QuickSectionProps> = ({ title, subtitle, icon, iconBg, items }) => (
  <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl p-8 border border-gray-200/50 dark:border-gray-700/50 shadow-sm">
    <div className="flex items-center gap-3 mb-6">
      <div className={`w-12 h-12 bg-gradient-to-br ${iconBg} rounded-2xl flex items-center justify-center`}>
        {icon}
      </div>
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h2>
        <p className="text-gray-600 dark:text-gray-400">{subtitle}</p>
      </div>
    </div>
    
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {items.map((item, index) => (
        <button
          key={index}
          onClick={item.onClick}
          className="group p-4 text-left bg-gray-50/50 dark:bg-gray-700/50 hover:bg-gradient-to-r hover:from-purple-50 hover:to-blue-50 dark:hover:from-purple-900/20 dark:hover:to-blue-900/20 rounded-xl border border-gray-200/50 dark:border-gray-600/50 hover:border-purple-200 dark:hover:border-purple-500/30 transition-all duration-300"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-700 dark:text-gray-300 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                {item.label}
              </div>
              {item.subtitle && (
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {item.subtitle}
                </div>
              )}
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-purple-500 group-hover:translate-x-1 transition-all" />
          </div>
        </button>
      ))}
    </div>
  </div>
);

// Extracted StatsSection component
interface StatsSectionProps {
  totalTools: number;
  toolCategoriesCount: number;
}

const StatsSection: React.FC<StatsSectionProps> = ({ totalTools, toolCategoriesCount }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
    <StatItem 
      icon={<Zap className="w-8 h-8 text-white" />}
      iconBg="from-blue-500 to-cyan-500"
      value={`${totalTools}+ Tools`}
      description="Comprehensive AI tools database"
    />
    
    <StatItem 
      icon={<Star className="w-8 h-8 text-white" />}
      iconBg="from-purple-500 to-pink-500"
      value={`${toolCategoriesCount}+ Categories`}
      description="Organized by use case and function"
    />
    
    <StatItem 
      icon={<Clock className="w-8 h-8 text-white" />}
      iconBg="from-green-500 to-emerald-500"
      value="Daily Updates"
      description="Fresh tools added regularly"
    />
  </div>
);

// Extracted StatItem component
interface StatItemProps {
  icon: React.ReactNode;
  iconBg: string;
  value: string;
  description: string;
}

const StatItem: React.FC<StatItemProps> = ({ icon, iconBg, value, description }) => (
  <div className="text-center">
    <div className={`w-16 h-16 bg-gradient-to-br ${iconBg} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
      {icon}
    </div>
    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{value}</h3>
    <p className="text-gray-600 dark:text-gray-400">{description}</p>
  </div>
);

// Extracted CTASection component
interface CTASectionProps {
  onBrowseCategories: () => void;
  onViewTools: () => void;
}

const CTASection: React.FC<CTASectionProps> = ({ onBrowseCategories, onViewTools }) => (
  <div className="text-center">
    <div className="bg-gradient-to-r from-purple-600/10 to-blue-600/10 dark:from-purple-600/20 dark:to-blue-600/20 rounded-3xl p-12 border border-purple-200/50 dark:border-purple-500/20">
      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
        Ready to explore?
      </h3>
      <p className="text-gray-600 dark:text-gray-400 mb-8 text-lg">
        Start searching or browse all categories to discover amazing AI tools
      </p>
      <div className="flex gap-4 justify-center flex-wrap">
        <button
          onClick={onBrowseCategories}
          className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
        >
          <Sparkles className="w-5 h-5" />
          Browse All Categories
        </button>
        <button
          onClick={onViewTools}
          className="inline-flex items-center gap-3 px-8 py-4 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-bold rounded-2xl transition-all duration-300 hover:scale-105"
        >
          <Hash className="w-5 h-5" />
          View All Tools
        </button>
      </div>
    </div>
  </div>
);

// Extracted SearchResultsPage component
interface SearchResultsPageProps {
  query: string;
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  onSearch: (e: React.FormEvent) => void;
  onClearSearch: () => void;
  searchResults: {
    tools: any[];
    categories: any[];
  };
  totalResults: number;
  isSearching: boolean;
  viewMode: 'grid' | 'list';
  onToggleViewMode: (mode: 'grid' | 'list') => void;
  onQuickSearch: (term: string) => void;
  onCategoryClick: (title: string) => void;
  onTagClick: (tag: string) => void;
  seoData: any;
}

const SearchResultsPage: React.FC<SearchResultsPageProps> = ({
  query,
  searchInput,
  onSearchInputChange,
  onSearch,
  onClearSearch,
  searchResults,
  totalResults,
  isSearching,
  viewMode,
  onToggleViewMode,
  onQuickSearch,
  onCategoryClick,
  onTagClick,
  seoData
}) => (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50/50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900/10 py-8">
    <Helmet>
      <title>{seoData.title}</title>
      <meta name="description" content={seoData.description} />
      <meta name="keywords" content={seoData.keywords} />
      <link rel="canonical" href={seoData.canonical} />
      
      {/* Open Graph */}
      <meta property="og:title" content={seoData.title} />
      <meta property="og:description" content={seoData.description} />
      <meta property="og:url" content={seoData.canonical} />
      <meta property="og:type" content="website" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoData.title} />
      <meta name="twitter:description" content={seoData.description} />

      {/* Structured Data for search results */}
      {seoData.structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(seoData.structuredData)}
        </script>
      )}
    </Helmet>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Search Header */}
      <SearchHeader 
        query={query}
        searchInput={searchInput}
        onSearchInputChange={onSearchInputChange}
        onSearch={onSearch}
        onClearSearch={onClearSearch}
        totalResults={totalResults}
        isSearching={isSearching}
        viewMode={viewMode}
        onToggleViewMode={onToggleViewMode}
      />

      {/* Loading State */}
      {isSearching && <LoadingState query={query} />}

      {/* Results */}
      {!isSearching && (
        <>
          {/* Categories Results */}
          {searchResults.categories.length > 0 && (
            <CategoriesResults 
              categories={searchResults.categories}
              onCategoryClick={onCategoryClick}
            />
          )}

          {/* Tools Results */}
          {searchResults.tools.length > 0 && (
            <ToolsResults 
              tools={searchResults.tools}
              viewMode={viewMode}
              onTagClick={onTagClick}
            />
          )}

          {/* No Results */}
          {searchResults.tools.length === 0 && searchResults.categories.length === 0 && !isSearching && (
            <NoResultsState 
              query={query}
              onQuickSearch={onQuickSearch}
            />
          )}
        </>
      )}
    </div>
  </div>
);

// Extracted SearchHeader component
interface SearchHeaderProps {
  query: string;
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  onSearch: (e: React.FormEvent) => void;
  onClearSearch: () => void;
  totalResults: number;
  isSearching: boolean;
  viewMode: 'grid' | 'list';
  onToggleViewMode: (mode: 'grid' | 'list') => void;
}

const SearchHeader: React.FC<SearchHeaderProps> = ({
  query,
  searchInput,
  onSearchInputChange,
  onSearch,
  onClearSearch,
  totalResults,
  isSearching,
  viewMode,
  onToggleViewMode
}) => (
  <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl p-8 border border-gray-200/50 dark:border-gray-700/50 shadow-sm mb-8">
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
      <div className="flex-1">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-2xl flex items-center justify-center">
            <Search className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Search Results
            </h1>
            <div className="flex items-center gap-3 flex-wrap mt-2">
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                Showing results for: 
              </p>
              <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-xl font-semibold text-lg">
                "{query}"
              </span>
              {!isSearching && totalResults > 0 && (
                <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-4 py-2 rounded-xl text-sm font-semibold">
                  {totalResults} {totalResults === 1 ? 'result' : 'results'} found
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Search Box in Results */}
        <form onSubmit={onSearch} className="max-w-2xl">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => onSearchInputChange(e.target.value)}
              placeholder="Refine your search..."
              className="w-full pl-12 pr-12 py-4 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-2xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg"
            />
            {searchInput && (
              <button
                type="button"
                onClick={onClearSearch}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </form>
      </div>

      {/* View Mode Toggle */}
      {totalResults > 0 && (
        <ViewModeToggle viewMode={viewMode} onToggle={onToggleViewMode} />
      )}
    </div>
  </div>
);

// Extracted ViewModeToggle component
interface ViewModeToggleProps {
  viewMode: 'grid' | 'list';
  onToggle: (mode: 'grid' | 'list') => void;
}

const ViewModeToggle: React.FC<ViewModeToggleProps> = ({ viewMode, onToggle }) => (
  <div className="flex items-center gap-4">
    <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-xl p-2">
      <button
        onClick={() => onToggle('grid')}
        className={`p-3 rounded-lg transition-all ${
          viewMode === 'grid'
            ? 'bg-white dark:bg-gray-600 shadow-sm text-purple-600 dark:text-purple-400'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
        }`}
        aria-label="Grid view"
      >
        <Grid className="w-5 h-5" />
      </button>
      <button
        onClick={() => onToggle('list')}
        className={`p-3 rounded-lg transition-all ${
          viewMode === 'list'
            ? 'bg-white dark:bg-gray-600 shadow-sm text-purple-600 dark:text-purple-400'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
        }`}
        aria-label="List view"
      >
        <List className="w-5 h-5" />
      </button>
    </div>
  </div>
);

// Extracted LoadingState component
interface LoadingStateProps {
  query: string;
}

const LoadingState: React.FC<LoadingStateProps> = ({ query }) => (
  <div className="text-center py-20">
    <div className="w-20 h-20 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
      Searching AI Tools
    </h3>
    <p className="text-gray-600 dark:text-gray-400 text-lg">
      Finding the best matches for "<span className="font-semibold">{query}</span>"...
    </p>
  </div>
);

// Extracted CategoriesResults component
interface CategoriesResultsProps {
  categories: any[];
  onCategoryClick: (title: string) => void;
}

const CategoriesResults: React.FC<CategoriesResultsProps> = ({ categories, onCategoryClick }) => (
  <section className="mb-12">
    <div className="flex items-center gap-4 mb-8">
      <div className="w-3 h-12 bg-gradient-to-b from-green-500 to-emerald-500 rounded-full"></div>
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Matching Categories
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {categories.length} categor{categories.length === 1 ? 'y' : 'ies'} found
        </p>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {categories.map((category, index) => (
        <button
          key={index}
          onClick={() => onCategoryClick(category.title)}
          className="group bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-sm border border-gray-200/50 dark:border-gray-700/50 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-left"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center text-white text-lg font-bold group-hover:scale-110 transition-transform">
              {category.title.charAt(0)}
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 dark:text-white text-lg mb-1 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
                {category.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {category.tools.length} {category.tools.length === 1 ? 'tool' : 'tools'}
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-green-500 group-hover:translate-x-1 transition-all" />
          </div>
        </button>
      ))}
    </div>
  </section>
);

// Extracted ToolsResults component
interface ToolsResultsProps {
  tools: any[];
  viewMode: 'grid' | 'list';
  onTagClick: (tag: string) => void;
}

const ToolsResults: React.FC<ToolsResultsProps> = ({ tools, viewMode, onTagClick }) => (
  <section>
    <div className="flex items-center gap-4 mb-8">
      <div className="w-3 h-12 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full"></div>
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          AI Tools
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {tools.length} tool{tools.length === 1 ? '' : 's'} found
        </p>
      </div>
    </div>
    <div className={viewMode === 'grid' 
      ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
      : "grid grid-cols-1 md:grid-cols-2 gap-6"
    }>
      {tools.map((tool, index) => (
        <ToolCard
          key={`${tool.category}-${index}`}
          {...tool}
          icon={tool.icon ?? '/default-icon.png'}
          category={tool.category}
          onTagClick={onTagClick}
        />
      ))}
    </div>
  </section>
);

// Extracted NoResultsState component
interface NoResultsStateProps {
  query: string;
  onQuickSearch: (term: string) => void;
}

const NoResultsState: React.FC<NoResultsStateProps> = ({ query, onQuickSearch }) => (
  <div className="text-center py-20">
    <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-inner">
      <Search className="w-12 h-12 text-gray-400" />
    </div>
    <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
      No results found for "{query}"
    </h3>
    <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/10 dark:to-purple-900/10 rounded-3xl p-8 mb-8 max-w-2xl mx-auto border border-gray-200/50 dark:border-gray-700/50">
      <p className="text-gray-700 dark:text-gray-300 mb-4 text-lg">
        We couldn't find any AI tools or categories matching your search. Try these suggestions:
      </p>
      <ul className="text-left text-gray-600 dark:text-gray-400 space-y-2 mb-6">
        <li>• Check your spelling and try again</li>
        <li>• Use more general keywords</li>
        <li>• Browse by category instead</li>
        <li>• Search for related terms</li>
      </ul>
    </div>
    <div className="space-y-4">
      <p className="text-gray-500 dark:text-gray-400 text-sm">
        Try these popular searches:
      </p>
      <div className="flex flex-wrap gap-3 justify-center">
        {POPULAR_SEARCHES.slice(0, 4).map((search, index) => (
          <button
            key={index}
            onClick={() => onQuickSearch(search)}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-purple-100 dark:hover:bg-purple-900/30 text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 rounded-xl transition-all duration-300 hover:scale-105"
          >
            {search}
          </button>
        ))}
      </div>
    </div>
  </div>
);

export default React.memo(SearchPage);